import { useState, type FormEvent } from "react";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/base/Breadcrumb";

const FORM_ENDPOINT = "https://readdy.ai/api/form/db8u1ok6oi3g3tv8uqi0";

const values = [
  {
    icon: "ri-shield-check-line",
    title: "只做正版信息聚合",
    desc: "尊重每一位创作者，本站仅作为韩漫信息的展示与阅读体验演示，不提供侵权下载。",
  },
  {
    icon: "ri-time-line",
    title: "紧跟最新章节",
    desc: "每日整理更新动态，让读者第一时间发现正在连载的精彩韩漫作品。",
  },
  {
    icon: "ri-emotion-happy-line",
    title: "阅读体验优先",
    desc: "清爽的阅读界面、可切换的背景与宽度设置，只为让你读得舒服、看得长久。",
  },
];

export default function About() {
  const [input, setInput] = useState({
    name: "",
    email: "",
    subject: "站点建议",
    message: "",
  });
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");

  const handleChange = (key: string, value: string) => {
    setInput((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = String(formData.get("website_alt") ?? "").trim();

    if (honeypot) {
      setStatus("success");
      setFormSuccess("感谢您的反馈，我们已经收到啦！");
      form.reset();
      setInput({ name: "", email: "", subject: "站点建议", message: "" });
      return;
    }

    formData.delete("website_alt");
    setStatus("submitting");
    setFormError("");
    setFormSuccess("");

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as [string, string][]).toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.includes("spam");
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus("success");
        setFormSuccess("感谢您的反馈，我们已经收到啦！");
        form.reset();
        setInput({ name: "", email: "", subject: "站点建议", message: "" });
      } else {
        setStatus("error");
        setFormError(
          serverMsg && typeof serverMsg === "string" && !isSpam
            ? serverMsg
            : "提交失败，请稍后重试。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <>
      <Seo
        title="关于我们 - 韩漫免费观看在线阅读漫画"
        description="了解韩漫免费观看在线阅读漫画网站的定位与运营理念、版权与免责声明，并通过反馈表单与我们取得联系。"
        keywords="关于韩漫,韩漫网站介绍,版权声明,免责声明,韩漫反馈"
      />

      <div className="mx-auto w-full max-w-5xl px-4 py-6 md:px-6 md:py-8">
        <Breadcrumb items={[{ label: "关于我们" }]} />

        <header className="mt-4">
          <h1 className="font-display text-2xl font-extrabold text-foreground-950 md:text-4xl">
            关于韩漫免费观看在线阅读漫画
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            我们是一个专注于韩漫在线阅读的爱好者平台，致力于把题材丰富、更新及时的韩漫作品整理到一起，让每一位读者都能免费、便捷地找到并阅读自己喜欢的漫画。
          </p>
        </header>

        <section className="mt-8 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
          <h2 className="text-lg font-bold text-foreground-950">网站定位与运营理念</h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600">
            本站定位为韩漫爱好者的一站式阅读入口，聚合爱情、奇幻、动作、校园、都市、悬疑、科幻等多元题材，并通过清晰分类、关键词搜索与最新更新列表，帮助读者高效发现内容。我们相信，好的阅读体验不该被付费墙和复杂操作打断，因此坚持免费开放、界面清爽、更新及时。
          </p>
        </section>

        <section className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
          {values.map((v) => (
            <div key={v.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                <i className={`${v.icon} text-xl`} />
              </span>
              <h3 className="mt-3 text-base font-semibold text-foreground-950">{v.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{v.desc}</p>
            </div>
          ))}
        </section>

        <section className="mt-6 rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
          <h2 className="text-lg font-bold text-foreground-950">版权声明与免责声明</h2>
          <div className="mt-3 space-y-3 text-sm leading-relaxed text-foreground-600">
            <p>
              <strong className="font-semibold text-foreground-900">版权声明：</strong>
              本站所有韩漫作品的著作权归原作者及出版方所有。本站不以任何形式提供付费服务，页面内容均为站点演示与信息整理用途。
            </p>
            <p>
              <strong className="font-semibold text-foreground-900">免责声明：</strong>
              本站所展示的漫画信息、封面与章节内容均为演示数据，仅供学习与浏览体验参考，不构成任何商业发行行为。如权利人认为本站内容侵犯其合法权益，请通过下方反馈表单联系我们，我们将及时处理。
            </p>
          </div>
        </section>

        <section id="contact" className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
          <h2 className="text-lg font-bold text-foreground-950">联系我们</h2>
          <p className="mt-2 text-sm text-foreground-600">
            对站点有任何建议、反馈或合作意向，欢迎留言告诉我们。
          </p>

          <form
            data-readdy-form
            id="about-contact-form"
            onSubmit={handleSubmit}
            className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2"
          >
            <div className="flex flex-col gap-1.5">
              <label htmlFor="name" className="text-sm text-foreground-700">
                您的称呼
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                value={input.name}
                onChange={(e) => handleChange("name", e.target.value)}
                placeholder="请输入您的称呼"
                className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="email" className="text-sm text-foreground-700">
                电子邮箱
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={input.email}
                onChange={(e) => handleChange("email", e.target.value)}
                placeholder="请输入您的邮箱"
                className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
              />
            </div>

            <div className="flex flex-col gap-1.5 md:col-span-2">
              <label htmlFor="subject" className="text-sm text-foreground-700">
                反馈类型
              </label>
              <select
                id="subject"
                name="subject"
                value={input.subject}
                onChange={(e) => handleChange("subject", e.target.value)}
                className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none"
              >
                <option value="站点建议">站点建议</option>
                <option value="漫画纠错">漫画纠错</option>
                <option value="版权问题">版权问题</option>
                <option value="商务合作">商务合作</option>
                <option value="其他">其他</option>
              </select>
            </div>

            <div className="flex flex-col gap-1.5 md:col-span-2">
              <label htmlFor="message" className="text-sm text-foreground-700">
                留言内容
              </label>
              <textarea
                id="message"
                name="message"
                required
                maxLength={500}
                rows={5}
                value={input.message}
                onChange={(e) => handleChange("message", e.target.value)}
                placeholder="请描述您的问题或建议（最多 500 字）"
                className="resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
              />
              <span className="self-end text-xs text-foreground-400">
                {input.message.length} / 500
              </span>
            </div>

            <input
              type="text"
              name="website_alt"
              className="form-consent-hidden"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />

            <div className="flex flex-wrap items-center gap-4 md:col-span-2">
              <button
                type="submit"
                disabled={status === "submitting"}
                className="whitespace-nowrap rounded-md bg-primary-500 px-7 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {status === "submitting" ? "提交中…" : "提交反馈"}
              </button>
              <p className="text-xs text-foreground-500">
                我们会在收到反馈后尽快处理，感谢你的支持。
              </p>
            </div>

            {status === "success" && formSuccess && (
              <p className="flex items-center gap-2 rounded-md bg-secondary-50 px-4 py-3 text-sm text-secondary-900 md:col-span-2">
                <i className="ri-checkbox-circle-line" />
                {formSuccess}
              </p>
            )}
            {status === "error" && formError && (
              <p className="flex items-center gap-2 rounded-md bg-accent-50 px-4 py-3 text-sm text-accent-900 md:col-span-2">
                <i className="ri-error-warning-line" />
                {formError}
              </p>
            )}
          </form>
        </section>
      </div>
    </>
  );
}